const normalizeRole = (value = '') => value.toLowerCase().replace(/[\s_-]+/g, '');
const normalizeKey = (value = '') => value.toLowerCase().replace(/[\s_-]+/g, '');

export const getRoleKey = (user) => normalizeRole(user?.roleType || user?.role);

export const isSuperAdmin = (user) => getRoleKey(user) === 'superadmin';
export const isAdmin = (user) => getRoleKey(user) === 'admin';
export const isManager = (user) => getRoleKey(user) === 'manager';
export const isEmployee = (user) => getRoleKey(user) === 'employee';

/**
 * ACTION-BASED PERMISSION SYSTEM
 * Check specific capabilities instead of just roles
 */

// Organization Actions
export const canViewOrganizations = (user) => hasPermission(user, 'organization', 'read');
export const canCreateOrganizations = (user) => hasPermission(user, 'organization', 'create');
export const canEditOrganization = (user) => hasPermission(user, 'organization', 'update');
export const canDeleteOrganization = (user) => hasPermission(user, 'organization', 'delete');
export const canManageOrganizations = (user) => canCreateOrganizations(user) && canEditOrganization(user) && canDeleteOrganization(user);

// Payslip Template Actions
export const canViewPayslipTemplates = (user) => hasPermission(user, 'payslipTemplates', 'read');
export const canCreatePayslipTemplate = (user) => hasPermission(user, 'payslipTemplates', 'create');
export const canEditPayslipTemplate = (user) => hasPermission(user, 'payslipTemplates', 'update');
export const canDeletePayslipTemplate = (user) => hasPermission(user, 'payslipTemplates', 'delete');
export const canManagePayslipTemplates = (user) => canCreatePayslipTemplate(user);

// Employee Actions
export const canViewEmployees = (user) => hasPermission(user, 'employees', 'read');
export const canCreateEmployee = (user) => hasPermission(user, 'employees', 'create');
export const canEditEmployee = (user) => hasPermission(user, 'employees', 'update');
export const canDeleteEmployee = (user) => hasPermission(user, 'employees', 'delete');
export const canManageEmployees = (user) => canCreateEmployee(user) || canEditEmployee(user);

// User Actions
export const canViewUsers = (user) => hasPermission(user, 'users', 'read');
export const canCreateUser = (user) => hasPermission(user, 'users', 'create');
export const canEditUser = (user) => hasPermission(user, 'users', 'update');
export const canDeleteUser = (user) => hasPermission(user, 'users', 'delete');
export const canManageUsers = (user) => canCreateUser(user) || canEditUser(user);

// Role Actions
export const canViewRoles = (user) => hasPermission(user, 'roles', 'read');
export const canCreateRole = (user) => hasPermission(user, 'roles', 'create');
export const canEditRole = (user) => hasPermission(user, 'roles', 'update');
export const canDeleteRole = (user) => hasPermission(user, 'roles', 'delete');
export const canManageRoles = (user) => canCreateRole(user) || canEditRole(user) || canDeleteRole(user);

// Holiday Actions
export const canViewHolidays = (user) => hasPermission(user, 'holidays', 'read');
export const canCreateHoliday = (user) => hasPermission(user, 'holidays', 'create');
export const canEditHoliday = (user) => hasPermission(user, 'holidays', 'update');
export const canDeleteHoliday = (user) => hasPermission(user, 'holidays', 'delete');
export const canManageHolidays = (user) => canCreateHoliday(user) || canEditHoliday(user) || canDeleteHoliday(user);

// Leave Actions
export const canViewLeaves = (user) => hasPermission(user, 'leaves', 'read');
export const canApplyLeave = (user) => hasPermission(user, 'leaves', 'create');
export const canApproveLeave = (user) => hasPermission(user, 'leaves', 'approve');
export const canRejectLeave = (user) => hasPermission(user, 'leaves', 'reject');
export const canApproveLeaves = (user) => canApproveLeave(user) || canRejectLeave(user);
export const canManageLeaves = (user) => canApplyLeave(user) || canApproveLeave(user);

// Attendance Actions
export const canViewAttendance = (user) => hasPermission(user, 'attendance', 'read');
export const canMarkAttendance = (user) => hasPermission(user, 'attendance', 'create');
export const canVerifyAttendance = (user) => hasPermission(user, 'attendance', 'verify');
export const canManageAttendance = (user) => canVerifyAttendance(user);

// Payroll Actions
export const canViewPayroll = (user) => hasPermission(user, 'payroll', 'read');
export const canCreatePayroll = (user) => hasPermission(user, 'payroll', 'create');
export const canUpdatePayroll = (user) => hasPermission(user, 'payroll', 'update');
export const canDeletePayroll = (user) => hasPermission(user, 'payroll', 'delete');
export const canProcessPayroll = (user) => hasPermission(user, 'payroll', 'process');
export const canGeneratePayroll = (user) => hasPermission(user, 'payroll', 'generate');
export const canDownloadPayroll = (user) => hasPermission(user, 'payroll', 'download');
export const canViewPayslips = (user) => hasPermission(user, 'payroll', 'read');
export const canManagePayroll = (user) => canCreatePayroll(user) || canUpdatePayroll(user) || canProcessPayroll(user) || canGeneratePayroll(user);

// Salary Structure Actions
export const canViewSalaryStructure = (user) => hasPermission(user, 'salaryStructure', 'read');
export const canReadSalaryStructure = (user) => hasPermission(user, 'salaryStructure', 'read');
export const canEditSalaryStructure = (user) => hasPermission(user, 'salaryStructure', 'update');
export const canManageSalaryStructure = (user) => canEditSalaryStructure(user);
export const canCreateSalaryStructure = (user) => hasPermission(user, 'salaryStructure', 'create');
export const canDeleteSalaryStructure = (user) => hasPermission(user, 'salaryStructure', 'delete');


// Settings Actions
export const canViewSettings = (user) => hasPermission(user, 'settings', 'read');
export const canEditSettings = (user) => hasPermission(user, 'settings', 'update');

// Leave Type Actions
export const canViewLeaveTypes = (user) => hasPermission(user, 'leaveTypes', 'read');
export const canCreateLeaveType = (user) => hasPermission(user, 'leaveTypes', 'create');
export const canEditLeaveType = (user) => hasPermission(user, 'leaveTypes', 'update');
export const canDeleteLeaveType = (user) => hasPermission(user, 'leaveTypes', 'delete');
export const canManageLeaveTypes = (user) => canCreateLeaveType(user) || canEditLeaveType(user) || canDeleteLeaveType(user);

// Documents Actions
export const canViewDocuments = (user) => hasPermission(user, 'documents', 'read');
export const canCreateDocument = (user) => hasPermission(user, 'documents', 'create');
export const canEditDocument = (user) => hasPermission(user, 'documents', 'update');
export const canDeleteDocument = (user) => hasPermission(user, 'documents', 'delete');
export const canManageDocuments = (user) => canCreateDocument(user) || canEditDocument(user) || canDeleteDocument(user);

// CTC Actions
export const canViewCTCs = (user) => hasPermission(user, 'ctc', 'read');
export const canCreateCTC = (user) => hasPermission(user, 'ctc', 'create');
export const canEditCTC = (user) => hasPermission(user, 'ctc', 'update');
export const canDeleteCTC = (user) => hasPermission(user, 'ctc', 'delete');
export const canManageCTC = (user) => canCreateCTC(user) || canEditCTC(user) || canDeleteCTC(user);

// Tax Declaration Actions
export const canViewTaxDeclaration = (user) => hasPermission(user, 'taxDeclaration', 'read');
export const canCreateTaxDeclaration = (user) => hasPermission(user, 'taxDeclaration', 'create');
export const canEditTaxDeclaration = (user) => hasPermission(user, 'taxDeclaration', 'update');
export const canDeleteTaxDeclaration = (user) => hasPermission(user, 'taxDeclaration', 'delete');
export const canManageTaxDeclaration = (user) => canCreateTaxDeclaration(user) || canEditTaxDeclaration(user) || canDeleteTaxDeclaration(user);

// Dashboard Actions
export const canViewDashboard = (user) => hasPermission(user, 'dashboard', 'read');

const getModulePermissions = (permissions = {}, moduleKey) => {
  if (!permissions || Array.isArray(permissions)) {
    return null;
  }

  const aliases = {
    payslipTemplates: ['payslipTemplate', 'payslipStructure', 'payslip_structure'],
    payslipTemplate: ['payslipTemplates', 'payslipStructure', 'payslip_structure'],
    salaryStructure: ['payrollStructure', 'salary_structure', 'payslipStructure', 'payslip_structure'],
    payrollStructure: ['salaryStructure', 'salary_structure']
  };
  const possibleKeys = [moduleKey, ...(aliases[moduleKey] || [])].map(normalizeKey);
  const entry = Object.entries(permissions).find(([key]) => possibleKeys.includes(normalizeKey(key)));
  return entry ? entry[1] : null;
};

const hasLegacyPermission = (permissions = {}, moduleKey, actionKey) => {
  const modules = Array.isArray(permissions.modules) ? permissions.modules : [];
  const actions = Array.isArray(permissions.actions) ? permissions.actions : [];

  return modules.some((module) => normalizeKey(module) === normalizeKey(moduleKey))
    && actions.some((action) => normalizeKey(action) === normalizeKey(actionKey));
};

export const hasPermission = (user, moduleKey, actionKey = 'read') => {
  if (isSuperAdmin(user)) {
    return true;
  }

  const permissions = user?.permissions || {};
  const modulePermissions = getModulePermissions(permissions, moduleKey);

  if (modulePermissions && typeof modulePermissions === 'object' && !Array.isArray(modulePermissions)) {
    const entry = Object.entries(modulePermissions).find(([key]) => normalizeKey(key) === normalizeKey(actionKey));
    if (entry) {
      return Boolean(entry[1]);
    }
  }

  return hasLegacyPermission(permissions, moduleKey, actionKey);
};

const FEATURE_MODULE_MAP = {
  organisation: 'organization',
  payslipTemplates: 'payslipTemplates',
  roles: 'roles',
  users: 'users',
  payrollStructure: 'salaryStructure',
  holidays: 'holidays',
  attendance: 'attendance',
  leavePolicy: 'leaves',
  fiscalYear: 'fiscalYear',
  settings: 'settings',
  employees: 'employees',
  leaves: 'leaves',
  payroll: 'payroll',
  dashboard: 'dashboard',
  ctc: 'ctc',
  documents: 'documents',
  taxDeclaration: 'tax-declaration'
};

const FEATURE_ACCESS_ACTIONS = [
  { action: 'create', letter: 'C' },
  { action: 'read', letter: 'R' },
  { action: 'update', letter: 'U' },
  { action: 'delete', letter: 'D' },
  { action: 'approve', letter: 'A' },
  { action: 'reject', letter: 'J' },
  { action: 'verify', letter: 'V' },
  { action: 'process', letter: 'P' }
];

const hasAnyModulePermission = (user, moduleKey) => FEATURE_ACCESS_ACTIONS.some(({ action }) => hasPermission(user, moduleKey, action));

export const getFeatureAccess = (user, featureKey) => {
  const moduleKey = FEATURE_MODULE_MAP[featureKey];
  if (!moduleKey) {
    return '-';
  }

  const access = FEATURE_ACCESS_ACTIONS
    .filter(({ action }) => hasPermission(user, moduleKey, action))
    .map(({ letter }) => letter)
    .join('');

  return access || '-';
};

export const canViewFeature = (user, featureKey) => {
  const moduleKey = FEATURE_MODULE_MAP[featureKey];
  return moduleKey ? hasAnyModulePermission(user, moduleKey) : false;
};

export const getFeatureAccessList = (user) => {
  const items = [
    { key: 'organisation', label: 'Organisation' },
    { key: 'payslipTemplates', label: 'Payslip Templates' },
    { key: 'roles', label: 'Roles' },
    { key: 'users', label: 'Users' },
    { key: 'payrollStructure', label: 'Payroll Structure' },
    { key: 'holidays', label: 'Holidays' },
    { key: 'attendance', label: 'Attendance' },
    { key: 'leavePolicy', label: 'Leave Policy' },
    { key: 'fiscalYear', label: 'Fiscal Year' },
    { key: 'settings', label: 'Settings' },
    { key: 'ctc', label: 'CTC' },
    { key: 'documents', label: 'Documents' },
    { key: 'taxDeclaration', label: 'Tax Declaration' }
  ];

  return items.map((item) => ({
    ...item,
    access: getFeatureAccess(user, item.key)
  }));
};
